import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { PhaseFrise } from "@/components/procedure/PhaseFrise";
import {
  deciderRecevabilite,
  getDossier,
  getRecevabilite,
  priseEnCharge,
} from "@/lib/apiProcedure";
import type {
  DossierProcedure,
  RecevabiliteChecklist,
  RecevabiliteDecision,
} from "@/types/procedure";

const CHECKLIST_LABELS: Record<keyof RecevabiliteChecklist, string> = {
  convention_arbitrage: "Convention d'arbitrage valable (AUA art. 3)",
  pieces_completes: "Bordereau de pièces complet",
  frais_ccja_payes: "Frais CCJA payés / échéancier",
  parties_identifiees: "Parties identifiées (AUA art. 4)",
  conflits_declares: "Absence de conflit bloquant",
};

export default function RecevabilitePage() {
  const [, params] = useRoute<{ id: string }>(
    "/greffe/cases/:id/recevabilite",
  );
  const id = params?.id;
  const [, navigate] = useLocation();

  const [dossier, setDossier] = useState<DossierProcedure | null>(null);
  const [decision, setDecision] = useState<RecevabiliteDecision | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [choix, setChoix] = useState<"RECEVABLE" | "IRRECEVABLE">("RECEVABLE");
  const [motif, setMotif] = useState("");
  const [checklist, setChecklist] = useState<RecevabiliteChecklist>({
    convention_arbitrage: false,
    pieces_completes: false,
    frais_ccja_payes: false,
    parties_identifiees: false,
    conflits_declares: true,
  });

  const reload = async () => {
    if (!id) return;
    const [resDossier, resDecision] = await Promise.all([
      getDossier(id),
      getRecevabilite(id).catch(() => null),
    ]);
    setDossier(resDossier.data);
    setDecision(resDecision?.data ?? null);
  };

  useEffect(() => {
    if (!id) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePriseEnCharge = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await priseEnCharge(id);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSubmitting(true);
    try {
      await deciderRecevabilite(id, {
        decision: choix,
        motif: choix === "IRRECEVABLE" ? motif : undefined,
        checklist,
      });
      if (choix === "RECEVABLE") navigate(`/greffe/cases/${id}/tribunal`);
      else await reload();
    } catch {
      alert("Erreur lors de l'enregistrement de la décision.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !dossier) return <div className="p-6">Chargement…</div>;
  const immuable = decision !== null;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <PhaseFrise currentPhase={dossier.phase_procedurale} />
      <h1 className="text-xl font-bold text-gray-800">
        Recevabilité — {dossier.reference}
      </h1>

      {dossier.phase_procedurale === "DEPOSE" && !immuable && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 flex justify-between items-center">
          <p className="font-medium text-yellow-800">
            Ce dossier nécessite une prise en charge par le greffe.
          </p>
          <button
            onClick={handlePriseEnCharge}
            disabled={submitting}
            className="bg-yellow-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Prendre en charge
          </button>
        </div>
      )}

      {dossier.phase_procedurale === "EN_RECEVABILITE" && !immuable && (
        <form
          onSubmit={handleDecision}
          className="bg-white p-6 rounded-lg shadow space-y-6"
        >
          <h2 className="text-lg font-semibold">
            Décision de recevabilité (AUA art. 3-4)
          </h2>
          <div className="space-y-3">
            <h3 className="font-medium text-gray-700">
              Checklist d'admissibilité
            </h3>
            {(Object.keys(checklist) as (keyof RecevabiliteChecklist)[]).map(
              (key) => (
                <label
                  key={key}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checklist[key]}
                    onChange={() =>
                      setChecklist({ ...checklist, [key]: !checklist[key] })
                    }
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm text-gray-600">
                    {CHECKLIST_LABELS[key]}
                  </span>
                </label>
              ),
            )}
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              Décision
            </span>
            <div className="flex space-x-4">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={choix === "RECEVABLE"}
                  onChange={() => setChoix("RECEVABLE")}
                />
                Recevable
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={choix === "IRRECEVABLE"}
                  onChange={() => setChoix("IRRECEVABLE")}
                />
                Irrecevable
              </label>
            </div>
          </div>

          {choix === "IRRECEVABLE" && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Motif (obligatoire, ≥ 10 caractères)
              </label>
              <textarea
                rows={4}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-gray-300"
                placeholder="Raison de l'irrecevabilité…"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? "Enregistrement…" : "Enregistrer la décision"}
          </button>
        </form>
      )}

      {immuable && decision && (
        <div
          className={`p-6 rounded-lg border-2 ${
            decision.decision === "RECEVABLE"
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          }`}
        >
          <h2 className="text-xl font-bold mb-2">
            Décision : {decision.decision}
          </h2>
          <p className="text-sm text-gray-500 mb-2">
            Rendue le {new Date(decision.created_at).toLocaleString()} par{" "}
            {decision.greffier_id}
          </p>
          {decision.motif && (
            <div className="mt-4 p-3 bg-white rounded border">
              <h3 className="font-medium text-gray-700">Motif :</h3>
              <p className="text-gray-800 whitespace-pre-wrap">
                {decision.motif}
              </p>
            </div>
          )}
          {decision.decision === "RECEVABLE" && (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/greffe/cases/${id}/parties`)}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Enrôler les parties &amp; conseils →
              </button>
              <button
                onClick={() => navigate(`/greffe/cases/${id}/tribunal`)}
                className="bg-white border border-blue-600 text-blue-600 px-4 py-2 rounded"
              >
                Constituer le tribunal →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
